import { LiveCreatorEventJournal } from '../clases/live-creator-journal';
import { LiveCanvasElement, LiveCreatorInvocationContext, LiveCreatorUserEvent } from '../interfaces/live-creator.contract';

type Canvas = LiveCreatorInvocationContext['canvas'];
interface Fingerprint { geometry: string; size: string; style: string }

/** Only revision counters and change fingerprints are retained, never a second Canvas. */
export class LiveCanvasState {
  readonly journal = new LiveCreatorEventJournal();
  revision = 0;
  savedRevision = 0;
  acknowledgedRevision = 0;
  private nodes = new Map<string, Fingerprint>();
  private connections = new Map<string, string>();
  private initialized = false;

  observe(canvas: Canvas, origin: 'user' | 'load' | 'agent' = 'user'): void {
    const events: LiveCreatorUserEvent[] = [];
    const nodes = new Map<string, Fingerprint>();
    for (const element of canvas.elements) {
      const fingerprint = this.fingerprint(element);
      nodes.set(element.id, fingerprint);
      const previous = this.nodes.get(element.id);
      if (!previous) events.push({ type: 'NODE_ADDED', dbId: element.dbId, payload: { ...element } });
      else {
        if (previous.geometry !== fingerprint.geometry) events.push({ type: 'NODE_MOVED', dbId: element.dbId,
          payload: { x: element.x, y: element.y, parentDbId: element.parentDbId, parentId: element.parentId } });
        if (previous.size !== fingerprint.size) events.push({ type: 'NODE_RESIZED', dbId: element.dbId,
          payload: { width: element.width, height: element.height } });
        if (previous.style !== fingerprint.style) events.push({ type: 'NODE_STYLE_CHANGED', dbId: element.dbId,
          payload: { shape: element.shape, color: element.color, textWidth: element.textWidth, dottedSpacing: element.dottedSpacing } });
      }
    }
    for (const [id, value] of this.nodes) if (!nodes.has(id)) {
      events.push({ type: 'NODE_REMOVED', dbId: JSON.parse(value.style)[0] });
    }
    const connections = new Map(canvas.connections.map(item => [item.relation, JSON.stringify(item)]));
    for (const item of canvas.connections) {
      const previous = this.connections.get(item.relation);
      if (previous !== connections.get(item.relation)) events.push({
        type: previous === undefined ? 'CONNECTION_ADDED' : 'CONNECTION_STYLE_CHANGED',
        relation: item.relation, payload: { ...item }
      });
    }
    for (const relation of this.connections.keys()) if (!connections.has(relation)) events.push({ type: 'CONNECTION_REMOVED', relation });
    if (origin === 'user' && this.initialized && events.length) {
      this.revision++;
      for (const event of events) this.journal.record(event);
    }
    this.nodes = nodes;
    this.connections = connections;
    this.initialized = true;
  }

  committed(canvas: Canvas): void {
    this.revision++;
    this.observe(canvas, 'agent');
  }

  markSaved(): void {
    this.savedRevision = this.revision;
    this.journal.record({ type: 'VIEW_SAVED', payload: { savedRevision: this.savedRevision } });
  }

  private fingerprint(element: LiveCanvasElement): Fingerprint {
    return {
      geometry: JSON.stringify([element.x, element.y, element.parentId, element.parentDbId]),
      size: JSON.stringify([element.width, element.height]),
      style: JSON.stringify([element.dbId, element.shape, element.color, element.textWidth, element.dottedSpacing, element.name])
    };
  }
}
